import React from "react";
import {
  Avatar,
  Box,
  Button,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import MoreVertOutlinedIcon from "@mui/icons-material/MoreVertOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import HubOutlinedIcon from "@mui/icons-material/HubOutlined";

import CrmShell from "../components/CrmShell";
import { CrmPanel, RoleChip, StatusChip, crmUi } from "../components/CrmUi";
import { activities, contacts, organizations } from "../demoData";

const relationCards = [
  ["Relation till UniQueue", "Nätverksorganisation", <AccountTreeOutlinedIcon />],
  ["Överordnad organisation", "Freelance Sweden", <ApartmentOutlinedIcon />],
  ["Debiteras av", "Freelance Sweden", <PaymentsOutlinedIcon />],
  ["Nätverksägare", "Reklambyrå Wilma", <HubOutlinedIcon />],
  ["Affärsnätverk", "Wilma Production Network", <AccountTreeOutlinedIcon />],
];

const CrmOrganizationDetailsPage = () => {
  const org = organizations.find(x => x.name === "Tryckeri Alfa") || organizations[0];

  return (
    <CrmShell>
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.8 }}>
        <Typography sx={{ color: "primary.main", fontSize: 10.4 }}>← Organisationer</Typography>
        <Typography sx={{ color: crmUi.subtle, fontSize: 10.4 }}>/</Typography>
        <Typography sx={{ color: crmUi.muted, fontSize: 10.4 }}>{org.name}</Typography>
      </Stack>

      <CrmPanel sx={{ overflow: "hidden" }}>
        <Box sx={{ p: { xs: 1.35, md: 1.6 } }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", md: "flex-start" }}
          >
            <Stack direction="row" spacing={1.1} alignItems="center">
              <Avatar sx={{ width: 42, height: 42, bgcolor: "#315f9d", fontSize: 14 }}>
                TA
              </Avatar>
              <Box>
                <Typography sx={{ color: crmUi.text, fontSize: 19, fontWeight: 560 }}>{org.name}</Typography>
                <Stack direction="row" spacing={0.6} alignItems="center" sx={{ mt: 0.25, flexWrap: "wrap" }}>
                  <RoleChip value="Leverantör" />
                  <Typography sx={{ color: crmUi.muted, fontSize: 9.8 }}>Organisations-ID: {org.id}</Typography>
                </Stack>
              </Box>
            </Stack>

            <Stack direction="row" spacing={0.6}>
              <Button size="small" variant="outlined" startIcon={<AddOutlinedIcon />} sx={{ textTransform: "none" }}>
                Ny aktivitet
              </Button>
              <Button size="small" variant="outlined" startIcon={<EditOutlinedIcon />} sx={{ textTransform: "none" }}>
                Redigera
              </Button>
              <IconButton size="small"><MoreVertOutlinedIcon /></IconButton>
            </Stack>
          </Stack>

          <Grid container spacing={0.85} sx={{ mt: 0.8 }}>
            {relationCards.map(([label, value, icon]) => (
              <Grid item xs={12} sm={6} lg key={label}>
                <Box sx={{ p: 1.05, border: `1px solid ${crmUi.border}`, borderRadius: 1.2, height: "100%" }}>
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <Box sx={{ color: "primary.main", "& svg": { fontSize: 17 } }}>{icon}</Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ color: crmUi.muted, fontSize: 9.2 }}>{label}</Typography>
                      <Typography sx={{ mt: 0.1, color: crmUi.text, fontSize: 10.6, fontWeight: 520 }} noWrap>
                        {value}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Tabs
          value={0}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ px: 1, minHeight: 38, borderTop: `1px solid ${crmUi.border}`, borderBottom: `1px solid ${crmUi.border}` }}
        >
          {["Översikt", "Aktiviteter", "Kontakter", "Affärer", "Avtal & priser", "Arbetsytor", "Filer"].map((label, i) => (
            <Tab key={label} label={i === 3 ? `${label} 3` : label} sx={{ minHeight: 38, textTransform: "none", fontSize: 10.5 }} />
          ))}
        </Tabs>

        <Grid container spacing={1} sx={{ p: 1.25 }}>
          <Grid item xs={12} lg={3}>
            <CrmPanel sx={{ p: 1.3, height: "100%", boxShadow: "none" }}>
              <Typography sx={{ color: crmUi.text, fontSize: 12.2, fontWeight: 550 }}>Om organisationen</Typography>
              <Stack spacing={1} sx={{ mt: 1.1 }}>
                {[
                  ["Typ", "Leverantör"],
                  ["Beskrivning", "Tryckeritjänster, digital- och offsettryck, efterbehandling."],
                  ["Webbplats", "www.tryckerialfa.se"],
                  ["Organisationsstatus", <StatusChip status="Aktiv" />],
                ].map(([label, value]) => (
                  <Box key={label}>
                    <Typography sx={{ color: crmUi.subtle, fontSize: 9.2 }}>{label}</Typography>
                    {React.isValidElement(value) ? (
                      <Box sx={{ mt: 0.3 }}>{value}</Box>
                    ) : (
                      <Typography sx={{ mt: 0.2, color: crmUi.text, fontSize: 10.6, lineHeight: 1.4 }}>{value}</Typography>
                    )}
                  </Box>
                ))}
              </Stack>
            </CrmPanel>
          </Grid>

          <Grid item xs={12} lg={5}>
            <CrmPanel sx={{ p: 1.3, height: "100%", boxShadow: "none" }}>
              <Typography sx={{ color: crmUi.text, fontSize: 12.2, fontWeight: 550 }}>Aktivitetshistorik</Typography>
              <List dense disablePadding sx={{ mt: 0.65 }}>
                {activities.slice(0, 4).map((item, i) => (
                  <React.Fragment key={item.id}>
                    <ListItem disableGutters alignItems="flex-start" sx={{ py: 0.65 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          mt: 0.75,
                          mr: 1,
                          borderRadius: "50%",
                          bgcolor: i === 0 ? "primary.main" : "#c7cdd6",
                          flexShrink: 0,
                        }}
                      />
                      <ListItemText
                        primary={item.title}
                        secondary={`${item.when} · ${item.organization}`}
                        primaryTypographyProps={{ fontSize: 10.5, color: crmUi.text }}
                        secondaryTypographyProps={{ fontSize: 9.4, color: crmUi.muted }}
                      />
                    </ListItem>
                    {i < 3 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
              <Button size="small" sx={{ mt: 0.5, px: 0, textTransform: "none", fontSize: 10.2 }}>
                Visa alla aktiviteter
              </Button>
            </CrmPanel>
          </Grid>

          <Grid item xs={12} lg={4}>
            <Stack spacing={1}>
              <CrmPanel sx={{ p: 1.3, boxShadow: "none" }}>
                <Typography sx={{ color: crmUi.text, fontSize: 12.2, fontWeight: 550 }}>Relaterade kontakter</Typography>
                <List dense disablePadding sx={{ mt: 0.45 }}>
                  {contacts.slice(0, 2).map(contact => (
                    <ListItem key={contact.id} disableGutters sx={{ py: 0.55 }}>
                      <ListItemAvatar sx={{ minWidth: 34 }}>
                        <Avatar sx={{ width: 28, height: 28, fontSize: 10 }}>
                          {contact.name.split(" ").map(x => x[0]).join("")}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={contact.name}
                        secondary={contact.title}
                        primaryTypographyProps={{ fontSize: 10.5, color: crmUi.text }}
                        secondaryTypographyProps={{ fontSize: 9.4, color: crmUi.muted }}
                      />
                    </ListItem>
                  ))}
                </List>
              </CrmPanel>

              <CrmPanel sx={{ p: 1.3, boxShadow: "none" }}>
                <Typography sx={{ color: crmUi.text, fontSize: 12.2, fontWeight: 550 }}>Relaterade organisationer</Typography>
                <Stack spacing={0.7} sx={{ mt: 0.8 }}>
                  {["Freelance Sweden", "Creative Partners", "Reklambyrå Wilma"].map(name => (
                    <Stack key={name} direction="row" spacing={0.7} alignItems="center">
                      <ApartmentOutlinedIcon sx={{ fontSize: 15, color: "primary.main" }} />
                      <Typography sx={{ color: crmUi.text, fontSize: 10.4 }}>{name}</Typography>
                    </Stack>
                  ))}
                </Stack>
              </CrmPanel>
            </Stack>
          </Grid>
        </Grid>
      </CrmPanel>
    </CrmShell>
  );
};

export default CrmOrganizationDetailsPage;
